import { Notification } from '@/common/types/databaseTypes';
import {
    getNotificationActualImageUrl,
    getNotificationDefaultIcon,
    getNotificationMessage,
    NotificationContent,
    NotificationType,
} from '../constants/notification';
import {
    Avatar,
    Box,
    Group,
    Overlay,
    rem,
    Stack,
    Text,
    UnstyledButton,
} from '@mantine/core';
import { useMantineTheme } from '@mantine/styles';
import { formatDistanceToNow } from 'date-fns';
import { useUpdateNotificationReadStatus } from '../services/useNotificationQueries';
import {
    IconCircle,
    IconCircleCheck,
    IconClipboardText,
} from '@tabler/icons-react';
import {
    useNotificationActionHandler,
    useNotificationTypeLabel,
} from '../utils/notificationUtils';
import {
    IconAlertCircle,
    IconAt,
    IconBell,
    IconMessages,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { getDateFnsLocale } from '@/common/utils/i18nUtils';

interface NotificationItemProps {
    notification: Notification;
    onClose: () => void;
}

export default function NotificationItem({
    notification,
    onClose,
}: NotificationItemProps) {
    const theme = useMantineTheme();
    const { t } = useTranslation('notification');

    const onClickNotification = useNotificationActionHandler();
    const updateReadStatus = useUpdateNotificationReadStatus();

    const handleClick = async () => {
        // 알림 클릭 처리
        await onClickNotification({
            type: notification.type as NotificationType,
            related_id: notification.related_id,
            content: notification.content,
        });

        // 읽음 상태로 변경
        if (!notification.is_read) {
            updateReadStatus.mutate({
                notificationId: notification.id,
                isRead: true,
            });
        }

        onClose();
    };

    // 알림 메시지 컴포넌트
    const notificationMessage = getNotificationMessage(
        notification.type as NotificationType,
        notification.content
    );

    // 알림 타입별 아이콘/이미지 렌더링
    const renderNotificationImage = () => {
        const imageUrl = getNotificationActualImageUrl(
            notification.content,
            notification.type as NotificationType
        );

        if (imageUrl) {
            return <Avatar src={imageUrl} size={32} />;
        }

        const { icon: IconComponent, colorName } = getNotificationDefaultIcon(
            notification.type as NotificationType
        );

        return (
            <Avatar color={colorName} size={32}>
                <IconComponent size={16} />
            </Avatar>
        );
    };

    // 알림 타입별 뱃지 렌더링 (아이콘/이미지 우측 하단에 배치)
    const renderBadge = () => {
        const type = notification.type as NotificationType;

        // 알림 타입에 따라 아이콘과 색상 결정
        let BadgeIcon = IconBell;
        let badgeColor = theme.colors.blue[6];

        switch (type) {
            case NotificationType.WORKSPACE_INVITE:
            case NotificationType.TEAM_INVITE:
                BadgeIcon = IconBell;
                badgeColor = theme.colors.blue[6];
                break;

            case NotificationType.WORKSPACE_OUT:
            case NotificationType.TEAM_OUT:
            case NotificationType.TODO_DEADLINE_REMINDER:
            case NotificationType.TODO_DEADLINE_CHANGED:
            case NotificationType.GOAL_DEADLINE_REMINDER:
            case NotificationType.GOAL_DEADLINE_CHANGED:
            case NotificationType.PROJECT_DEADLINE_REMINDER:
            case NotificationType.PROJECT_DEADLINE_CHANGED:
            case NotificationType.PLAN_REQUEST:
                BadgeIcon = IconAlertCircle;
                badgeColor = theme.colors.red[6];
                break;

            case NotificationType.COMMENT:
            case NotificationType.REPLY:
                BadgeIcon = IconMessages;
                badgeColor = theme.colors.teal[6];
                break;

            case NotificationType.MENTION:
                BadgeIcon = IconAt;
                badgeColor = theme.colors.yellow[6];
                break;

            case NotificationType.REPORT_RECEIVE:
            case NotificationType.REPORT_REQUEST:
            case NotificationType.REPORT_UPDATED:
                BadgeIcon = IconClipboardText;
                badgeColor = theme.colors.orange[6];
                break;

            default:
                break;
        }

        return (
            <Box
                style={{
                    position: 'absolute',
                    bottom: '-4px',
                    right: '-4px',
                    width: rem(16),
                    height: rem(16),
                    borderRadius: '50%',
                    color: theme.white,
                    backgroundColor: badgeColor,
                    border: `1px solid ${theme.white}`,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
            >
                <BadgeIcon size={10} />
            </Box>
        );
    };

    return (
        <Group justify="space-between" p="sm" style={{ position: 'relative' }}>
            {notification.is_read && (
                <Overlay
                    color="#fff"
                    backgroundOpacity={0.5}
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        pointerEvents: 'none', // 클릭 이벤트 통과 (오버레이로 인해 클릭 막히지 않게)
                    }}
                />
            )}
            <Group gap={0} align="start">
                {
                    <Box
                        w={8}
                        h={8}
                        style={{
                            borderRadius: '50%',
                            backgroundColor: !notification.is_viewed
                                ? theme.colors.orange[5]
                                : 'transparent',
                        }}
                    />
                }
                <Box style={{ position: 'relative' }}>
                    {renderNotificationImage()}
                    {renderBadge()}
                </Box>
            </Group>
            <Box style={{ flex: 1, minWidth: 0 }}>
                <Stack gap={0}>
                    <UnstyledButton onClick={() => void handleClick()} w="100%">
                        <Group gap={0}>
                            <Text fz={11} fw={600} c="blue.5">
                                {useNotificationTypeLabel(
                                    notification.type as NotificationType,
                                    notification.content as NotificationContent
                                )}
                            </Text>
                            <Text fz={11} c="dimmed" px={4}>
                                •
                            </Text>
                            <Text fz={11} c="dimmed">
                                {t('notification:timeAgo', {
                                    time: formatDistanceToNow(
                                        new Date(notification.register_date),
                                        {
                                            addSuffix: false,
                                            locale: getDateFnsLocale(),
                                        }
                                    ),
                                })}
                            </Text>
                        </Group>
                        <Stack gap={4}>
                            <Text fz="xs" lineClamp={3}>
                                {notificationMessage.message}
                            </Text>

                            {/* 추가 요소 */}
                            {notificationMessage.additionalContent && (
                                <Box>
                                    {notificationMessage.additionalContent}
                                </Box>
                            )}
                        </Stack>
                    </UnstyledButton>
                </Stack>
            </Box>
            <UnstyledButton
                size="xs"
                onClick={() =>
                    updateReadStatus.mutate({
                        notificationId: notification.id,
                        isRead: !notification.is_read,
                    })
                }
            >
                {notification.is_read ? (
                    <IconCircleCheck size={16} color={theme.colors.gray[6]} />
                ) : (
                    <IconCircle size={16} color={theme.colors.gray[6]} />
                )}
            </UnstyledButton>
        </Group>
    );
}
